import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createValidationEngine } from '../ValidationEngine';
import type { UseValidationEngineOptions, ValidationEngine } from '../ValidationEngine.interface';
import type { ValidationContext, ValidationResult } from '../types';
import { deepHasError } from '../utils/errorState';
import { deepClonePlain } from '../utils/plainObjects';

export type { DispatchMode, MergeStrategy } from '../types';
export type { UseValidationEngineOptions } from '../ValidationEngine.interface';
export { createValidationEngine };

/**
 * Options that control the nested error-state shape produced from the engine.
 *
 * This matches `ValidationEngine.getFormErrorState()` but intentionally omits `refresh` because this hook can compute
 * either a cached read (fast) or a refresh (re-validate) via `refreshFormErrors()`.
 */
export type FormErrorStateOptions = {
    channel?: keyof ValidationResult['channels'];
    includePaths?: string[];
    onlyActive?: boolean;
    flow?: ValidationContext['flow'];
};

/**
 * Form-level hook that returns a stable engine instance across renders.
 *
 * This hook only manages engine lifetime; it does not maintain any React state derived from the engine.
 * If you want a React-managed `formErrors` object that updates automatically, use
 * `useFormValidationWithState()`.
 */
export function useFormValidation(options?: UseValidationEngineOptions): ValidationEngine {
    const engineRef = useRef<ValidationEngine | null>(null);

    if (!engineRef.current) {
        engineRef.current = createValidationEngine(options);
    }

    return engineRef.current;
}

export type UseFormValidationWithStateResult = {
    /**
     * A stable validation engine instance whose *mutating* methods are wrapped to keep `formErrors` in sync.
     *
     * Note: This is a shallow wrapper; if a consumer mutates `engine.ctx.values` directly, React state will not
     * automatically update. Prefer using engine mutators (`setValue`, `setValues`, etc.).
     */
    engine: ValidationEngine;
    /**
     * Nested error-state tree shaped by `ValidationEngine.getFormErrorState()`.
     * Leaves are `{ hasError: boolean, errorMessage?: string }`.
     */
    formErrors: any;
    /**
     * React-managed snapshot of `engine.ctx.values`.
     *
     * This stays in sync when consumers use engine mutators (`setValue`, `setValues`, etc.).
     * If a consumer mutates `engine.ctx.values` directly, React state will not automatically update.
     */
    formValues: any;
    /**
     * Convenience boolean computed from `formErrors`.
     * True when any nested leaf has `hasError: true`.
     */
    hasErrors: boolean;
    /**
     * Recomputes and stores `formErrors`.
     * - `refresh: true` forces validation based on current `ctx.values` and registered rules.
     * - `refresh: false` reads from cache when possible.
     */
    refreshFormErrors: (options?: { refresh?: boolean }) => any;
};

/**
 * Returns a stable validation engine instance plus a React-managed `formErrors` object.
 *
 * The returned engine is a wrapped proxy that updates `formErrors` whenever core engine methods are called
 * (validation, error mutation, values mutation, or registration).
 */
export function useFormValidationWithState(
    engineOptions?: UseValidationEngineOptions,
    errorStateOptions?: FormErrorStateOptions
): UseFormValidationWithStateResult {
    // The underlying engine instance should remain stable for the lifetime of the component.
    // We intentionally do not re-create the engine if `engineOptions` changes, mirroring `useFormValidation()`.
    const engineRef = useRef<ValidationEngine | null>(null);
    if (!engineRef.current) {
        engineRef.current = createValidationEngine(engineOptions);
    }

    // Keep the latest errorStateOptions available without making `computeFormErrors` depend on object identity.
    // Many call sites pass object literals.
    const errorStateOptionsRef = useRef<FormErrorStateOptions>({});
    errorStateOptionsRef.current = errorStateOptions ?? {};

    /**
     * Produces a nested form error state from the engine.
     *
     * IMPORTANT: This reads through the *base* engine (not the wrapped engine) to avoid accidental recursion.
     */
    const computeFormErrors = useCallback((refresh: boolean) => {
        const engine = engineRef.current as ValidationEngine;
        const opts = errorStateOptionsRef.current;
        const channel = opts.channel ?? 'inline';
        return engine.getFormErrorState({
            channel,
            includePaths: opts.includePaths,
            onlyActive: opts.onlyActive,
            flow: opts.flow,
            refresh
        });
    }, []);

    /**
     * Produces a cloned values snapshot from the engine.
     *
     * IMPORTANT: We clone to ensure React sees a new reference even when the engine mutates nested values in place.
     * This hook is designed for plain data (strings/numbers/booleans/objects/arrays) stored in `ctx.values`.
     */
    const computeFormValues = useCallback(() => {
        const engine = engineRef.current as ValidationEngine;
        return deepClonePlain(engine.ctx.values);
    }, []);

    // Initialize from cached values (no refresh) so first render stays cheap.
    const [formErrors, setFormErrors] = useState<any>(() => computeFormErrors(false));

    // Initialize from current engine values.
    const [formValues, setFormValues] = useState<any>(() => computeFormValues());

    // Derived boolean is memoized to avoid re-walking the tree unnecessarily.
    const hasErrors = useMemo(() => deepHasError(formErrors), [formErrors]);

    /**
     * Recomputes `formErrors` without forcing a validation refresh.
     * Used by the wrapped engine methods after they mutate engine state.
     */
    const syncFormErrors = useCallback(() => {
        setFormErrors(computeFormErrors(false));
    }, [computeFormErrors]);

    /**
     * Recomputes `formValues` from the engine.
     * Used by the wrapped value mutators.
     */
    const syncFormValues = useCallback(() => {
        setFormValues(computeFormValues());
    }, [computeFormValues]);

    /**
     * Public imperative refresh API.
     * Useful for submit flows where you want to validate everything and then render a single updated error tree.
     */
    const refreshFormErrors = useCallback(
        (options?: { refresh?: boolean }) => {
            const next = computeFormErrors(options?.refresh ?? true);
            setFormErrors(next);
            return next;
        },
        [computeFormErrors]
    );

    // Sync when the effective options change (not just when the object identity changes).
    // Many call sites pass object literals; using `[errorStateOptions]` can cause a re-render loop:
    // render -> new options object -> effect -> setState -> render -> ...
    //
    // We intentionally convert to primitive keys:
    // - channel/flow/onlyActive are stable primitives
    // - includePaths is keyed by content order (callers should pass a stable order)
    const channelKey = (errorStateOptions?.channel ?? 'inline') as string;
    const onlyActiveKey = Boolean(errorStateOptions?.onlyActive);
    const flowKey = (errorStateOptions?.flow ?? '') as string;
    const includePathsKey = (errorStateOptions?.includePaths ?? []).join('\u0000');

    useEffect(() => {
        syncFormErrors();
    }, [syncFormErrors, channelKey, onlyActiveKey, flowKey, includePathsKey]);

    // Wrap core mutating methods so any engine updates also update React state.
    // This wrapper preserves object identity across renders (important for consumers using referential equality).
    const wrappedEngineRef = useRef<ValidationEngine | null>(null);
    if (!wrappedEngineRef.current) {
        const base = engineRef.current;

        wrappedEngineRef.current = {
            ...base,
            // Registration mutates the engine's rule store and can change the default seeded error shape.
            registerRules: (path, rules, opts) => {
                base.registerRules(path, rules, opts);
                syncFormErrors();
            },
            registerConfig: (config, opts) => {
                base.registerConfig(config, opts);
                syncFormErrors();
            },
            // Value mutations may invalidate cached errors.
            setValue: (path, value, options) => {
                const res = base.setValue(path, value, options);
                syncFormValues();
                syncFormErrors();
                return res;
            },
            setValues: (values, options) => {
                base.setValues(values, options);
                syncFormValues();
                syncFormErrors();
            },
            setOptional: (path, optional) => {
                base.setOptional(path, optional);
                syncFormErrors();
            },
            setFlow: (flow) => {
                base.setFlow(flow);
                syncFormErrors();
            },
            setError: (path, isValid, message) => {
                base.setError(path, isValid, message);
                syncFormErrors();
            },
            // Validation calls may update the engine's internal error cache.
            validatePath: (path, value) => {
                const res = base.validatePath(path, value);
                syncFormErrors();
                return res;
            },
            validateAll: () => {
                const res = base.validateAll();
                syncFormErrors();
                return res;
            },
            commit: (pathsOverride, commitOptions) => {
                base.commit(pathsOverride, commitOptions);
                syncFormErrors();
            },
            clearErrors: (path) => {
                base.clearErrors(path);
                syncFormErrors();
            }
        } as ValidationEngine;
    }

    return { engine: wrappedEngineRef.current, formErrors, formValues, hasErrors, refreshFormErrors };
}
